<template>
  <article id="CUSTOMER_SEARCH" class="container-fixed offset-bottom-4">
    <header>
      <div v-if="errorSelectCustomer">
        <p />
        <div class="notification notification-negative" role="alert">
          <h3 class="notification-heading">{{ $t('common.headerError') }}</h3>
          <div class="notification-content">
            {{ $t('common.errorSelectCustomer') }}
          </div>
        </div>
      </div>
    </header>

    <section>
      <VTabset label="common.customerSearchHeader">
        <div class="row">
          <div class="col-l-5 col-m-12">
            <VTab label="common.customerNo" name="formCustomer">
              <VForm :form="formCustomer" @submit="onSubmit">
                <VInput name="customerNo" required :label="$t('common.customerNo')" :hint="$t('common.customerNoHint')" />

                <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default">{{ $t('common.btnSearch') }}</button>
              </VForm>
            </VTab>
            <VTab label="common.phone" name="formPhone">
              <VForm :form="formPhone" @submit="onSubmit">
                <VInput name="precallnumber" required :label="$t('common.precallNo')" :hint="$t('common.precallNoHint')" />

                <VInput name="callnumber" required :label="$t('common.phone')" :hint="$t('common.phoneHint')" />

                <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default">{{ $t('common.btnSearch') }}</button>
              </VForm>
            </VTab>

            <VTab v-if="isSearchByIdentityAllowed" label="common.identity" name="formIdentity">
              <VForm :form="formIdentity" @submit="onSubmit">
                <VInput name="identity" required :label="$t('common.identityValue')" :hint="$t('common.identityHint')" />

                <VSelect name="identityType" :label="$t('common.identityType')" />

                <button :disabled="searchform.isSearchLoading" type="submit" class="btn btn-default">{{ $t('common.btnSearch') }}</button>
              </VForm>
            </VTab>
          </div>
          <div class="col-l-7 col-m-12">
            <VSearchFormStatus v-bind="searchform" class="offset-top-1">
              {{ $t('common.customerSearchMessageSuccess', { customerNo }) }}
            </VSearchFormStatus>
          </div>
        </div>
      </VTabset>
    </section>
    <p />
  </article>
</template>

<script>
import {mapGetters, mapState} from 'vuex';
import Form from '@/components/Form/Form';
import Input from '@/components/Form/Input';
import Select from '@/components/Form/Select';
import {focusFirst, hasMinLength, initFields, match} from '@/utils/form';
import TitleMixin from '@/mixins/title';
import SearchFormMixin from '@/mixins/searchform';
import {getErrorMessageKey} from '@/utils/i18n';
import {CONTRACT_SEARCH} from '@route-types';

export default {
  name: 'CustomerSearch',

  components: {
    VTabset: () => import('@/components/UI/Tabs/Tabset'),
    VTab: () => import('@/components/UI/Tabs/Tab'),
    VSearchFormStatus: () => import('@/components/SearchFormStatus'),
    VForm: Form,
    VInput: Input,
    VSelect: Select,
  },

  mixins: [TitleMixin, SearchFormMixin],

  props: {
    errorSelectCustomer: {
      type: Boolean,
      required: false,
    },

    originalToName: {
      type: String,
      required: false,
      default: null,
    },

    originalToParams: {
      type: Object,
      required: false,
      default: null,
    },
  },

  data() {
    return {
      formCustomer: initFields({ name: 'customerNo', validators: hasMinLength(2) }),
      formPhone: initFields(
        {
          name: 'precallnumber',
          validators: [{ handler: match(/^[0-9]*$/), hint: 'common.invalidPrecallnumber' }, hasMinLength(2)],
        },
        {
          name: 'callnumber',
          validators: [{ handler: match(/^[1-9]{1}[0-9]*$/), hint: 'common.invalidCallnumber' }, hasMinLength(3)],
        }
      ),
      formIdentity: initFields(
        { name: 'identity', validators: hasMinLength(2) },
        {
          name: 'identityType',
          items: ['GUID', 'ANID', 'TLNR', 'E-Mail'].map((s) => ({ id: s, label: s })),
          value: this.$route.params.memberIdType,
        }
      ),
    };
  },

  computed: {
    ...mapState('customer', ['customerNo']),

    ...mapGetters('system', ['isContractSearchAllowed', 'isSearchByIdentityAllowed']),
  },

  methods: {
    async onSubmit({ form, values }) {
      this.searchLoading();

      try {
        const data = await this.$store.dispatch('customer/search', { form: values });
        if (data.customerNo === null) {
          this.searchErrorNoData();
        } else {
          this.searchSuccess();

          if (this.originalToName) {
            this.$router.push({
              name: this.originalToName,
              params: this.originalToParams,
            });
            return;
          }

          if (this.isContractSearchAllowed) {
            // route to contract search
            this.$router.push({
              name: CONTRACT_SEARCH,
              params: {},
            });
          }
        }
      } catch (err) {
        focusFirst(form);
        switch (err.status) {
          case 404:
            this.setErrorCode('errorcodes.NO_CUSTOMER_FOUND');
            break;
          case 400:
            this.searchErrorInvalidFormData(err?.data?.errorDetails?.errorMapping);
            break;
          default:
            this.setErrorCode(getErrorMessageKey(err));
        }
      }
    },
  },
};
</script>
